<template>
  <div style="display: contents;">
    <!-- Notification Dropdown -->
    <b-nav-item-dropdown class="d-flex align-items-center navbar-action-item notification-dropdown" right no-caret menu-class="notification-dropdown-menu">
      <template #button-content>
        <span class="notification-trigger" v-b-tooltip.hover title="Notifications">
          <i class="ri-notification-3-line"></i>
          <span
            v-if="totalUnreadCount"
            class="notification-badge"
            :class="{ 'notification-badge-pulse': hasAnyDanger }">
            {{ totalUnreadCount > 99 ? '99+' : totalUnreadCount }}
          </span>
        </span>
      </template>

      <div class="notification-panel">
        <div class="notification-header">
          <span>Notifications</span>
          <span v-if="totalUnreadCount" class="notification-count-pill">
            {{ totalUnreadCount }}
          </span>
        </div>

        <div v-if="isAnyNotificationLoading && !totalUnreadCount" class="notification-empty">
          <b-spinner small variant="primary" />
          <p>Loading notifications...</p>
        </div>

        <div v-else-if="!totalUnreadCount" class="notification-empty">
          <i class="ri-check-double-line"></i>
          <p>You're all caught up</p>
        </div>

        <div v-else class="notification-list">
          <!-- Expiration Section -->
          <div v-if="notifications.length" class="notification-section">
            <button
              type="button"
              class="notification-section-header"
              :aria-expanded="sectionExpanded.expiration ? 'true' : 'false'"
              @click.stop.prevent="toggleSection('expiration')">
              <span class="notification-section-title">Subscription Expiration</span>
              <span class="notification-section-meta">
                <span class="notification-section-count">{{ unreadNotificationsCount }}</span>
                <span
                  class="notification-section-arrow"
                  :class="{ collapsed: !sectionExpanded.expiration }"
                  aria-hidden="true">&#9662;</span>
              </span>
            </button>
            <div v-show="sectionExpanded.expiration">
              <b-dropdown-item
                v-for="notification in notifications"
                :key="notification.id"
                class="notification-item notification-item-unread"
                :class="`notification-${notification.severity}`"
                @click="handleNotifClick(notification)">
                <div class="notification-status" aria-hidden="true"></div>
                <div class="notification-content">
                  <div v-if="notification.shipName" class="notification-ship">{{ notification.shipName }}</div>
                  <div class="notification-title">{{ notification.title }}</div>
                  <div class="notification-message">{{ notification.message }}</div>
                  <div class="notification-time">Expired at: {{ formatNotifDate(notification.time) }}</div>
                </div>
              </b-dropdown-item>
            </div>
          </div>

          <!-- Alerts Section -->
          <div v-if="canRead('alert_incident') && alertNotifications.length" class="notification-section">
            <button
              type="button"
              class="notification-section-header"
              :aria-expanded="sectionExpanded.alerts ? 'true' : 'false'"
              @click.stop.prevent="toggleSection('alerts')">
              <span class="notification-section-title">Alerts</span>
              <span class="notification-section-meta">
                <span class="notification-section-count">{{ alertUnreadCount }}</span>
                <span
                  class="notification-section-arrow"
                  :class="{ collapsed: !sectionExpanded.alerts }"
                  aria-hidden="true">&#9662;</span>
              </span>
            </button>
            <div v-show="sectionExpanded.alerts">
              <b-dropdown-item
                v-for="notification in alertNotifications"
                :key="notification.id"
                class="notification-item notification-item-unread"
                :class="`notification-${notification.severity}`"
                @click="handleNotifClick(notification)">
                <div class="notification-status" aria-hidden="true"></div>
                <div class="notification-content">
                  <div v-if="notification.shipName" class="notification-ship">{{ notification.shipName }}</div>
                  <div class="notification-title">
                    {{ notification.title }}
                    <b-badge
                      :variant="notification.status === 'Active' ? 'danger' : 'secondary'"
                      class="ml-1 align-middle event-notif-badge">
                      {{ notification.status }}
                    </b-badge>
                  </div>
                  <div class="notification-time">At: {{ formatAlertTime(notification.time) }}</div>
                </div>
              </b-dropdown-item>
            </div>
          </div>
        </div>

        <div v-if="notifications.length" class="notification-footer-group">
          <button
            type="button"
            class="notification-footer notification-footer-split"
            @click.stop="handleViewAll">
            Expired Assets
          </button>
        </div>
      </div>
    </b-nav-item-dropdown>
    <!--/ Notification Dropdown -->
  </div>
</template>

<script>
import moment from 'moment';
import { formatApiDatetime, DISPLAY_DATE } from '@/util/apiDatetime';

export default {
  name: 'NavbarNotifications',
  data() {
    return {
      notificationPollMs: 5 * 60 * 1000,
      alertPollMs: 60 * 1000,
      sectionExpanded: {
        expiration: true,
        alerts: true,
      },
    };
  },
  computed: {
    notifications() {
      return this.$store.getters['notification/notifications'];
    },
    unreadNotificationsCount() {
      return this.$store.getters['notification/unreadCount'];
    },
    isNotificationLoading() {
      return this.$store.getters['notification/loading'];
    },
    alertNotifications() {
      return this.$store.getters['alertIncident/notifications'];
    },
    alertUnreadCount() {
      return this.$store.getters['alertIncident/unreadCount'];
    },
    isAlertLoading() {
      return this.$store.getters['alertIncident/loading'];
    },
    totalUnreadCount() {
      return this.unreadNotificationsCount + this.alertUnreadCount;
    },
    isAnyNotificationLoading() {
      return this.isNotificationLoading || this.isAlertLoading;
    },
    hasAnyDanger() {
      return this.notifications.some((n) => n.severity === 'danger')
        || this.alertNotifications.some((n) => n.severity === 'danger');
    },
  },
  mounted() {
    this.$store.dispatch('notification/fetch');
    this._notifPoll = setInterval(() => {
      this.$store.dispatch('notification/fetch');
    }, this.notificationPollMs);

    if (this.canRead('alert_incident')) {
      this.$store.dispatch('alertIncident/fetch');
      this._alertPoll = setInterval(() => {
        this.$store.dispatch('alertIncident/fetch');
      }, this.alertPollMs);
    }
  },
  beforeDestroy() {
    if (this._notifPoll) clearInterval(this._notifPoll);
    if (this._alertPoll) clearInterval(this._alertPoll);
  },
  methods: {
    // massSubscriptionExpiredAt itu tanggal kalender, bukan instan — jangan dikonversi zona.
    formatNotifDate(dateStr) {
      const d = moment(String(dateStr || '').slice(0, 10), 'YYYY-MM-DD', true);
      return d.isValid() ? d.format(DISPLAY_DATE) : '-';
    },
    formatAlertTime: formatApiDatetime,
    handleNotifClick(notification) {
      if (notification.massId && this.$route.name !== 'Asset') {
        this.$router.push({
          name: 'Asset',
          query: { highlight: notification.massId },
        });
      }
    },
    handleViewAll() {
      if (this.$route.name === 'Asset' && this.$route.query.subscriptionFilter === 'expired') return;
      this.$router.push({
        name: 'Asset',
        query: { subscriptionFilter: 'expired' },
      });
    },
    toggleSection(name) {
      this.sectionExpanded = {
        ...this.sectionExpanded,
        [name]: !this.sectionExpanded[name],
      };
    },
  },
};
</script>
